import { useFlags } from "launchdarkly-react-client-sdk";
import { type ReactNode, useState } from "react";
import { BaseButton } from "../../../../../../components/Button/BaseButton.tsx";
import {
  Icon,
  type IconName,
} from "../../../../../../components/Icon/Icon.tsx";
import {
  ANIMATE_TOOL,
  EDIT_ASSET_TOOL,
  EDIT_IMAGE_WITH_PROMPT_TOOL,
  EXPAND_TOOL,
  FILL_TOOL,
  UPSCALE_TOOL,
  VARIATION_TOOL,
} from "../../../../../../routes.ts";
import { useSelectedTool } from "../../../../hooks/useSelectedTool.ts";
import { InformationSection } from "../../AssetDetails/components/InformationSection/InformationSection.tsx";
import { AnimateSettings } from "./Animate/AnimateSettings.tsx";
import { EditImageWithPromptSettings } from "./EditImageWithPrompt/EditImageWithPromptSettings.tsx";
import { ExpandSettings } from "./Expand/ExpandSettings.tsx";
import { GenerativeFillSettings } from "./GenerativeFill/GenerativeFillSettings.tsx";
import { UpscaleSettings } from "./Upscale/UpscaleSettings.tsx";
import { VariationSettings } from "./Variation/VariationSettings.tsx";

export const EditAssetSettings = () => {
  const { selectedTool, setSelectedTool } = useSelectedTool();

  const flags = useFlags();

  // FIXME: This state should be merge with tool route logic at some point
  // so we have a single state that determines which section is open
  const [infoSectionOpen, setInfoSectionOpen] = useState(false);

  return (
    <div className="h-full flex-col items-center p-200 gap-200 overflow-y-auto scrollbar-hide">
      <WrappedToolSection
        title="Edit"
        iconName="SquarePen"
        isSectionOpen={selectedTool === EDIT_IMAGE_WITH_PROMPT_TOOL}
        onClick={(open) => {
          setSelectedTool(open ? EDIT_ASSET_TOOL : EDIT_IMAGE_WITH_PROMPT_TOOL);
        }}
      >
        <EditImageWithPromptSettings />
      </WrappedToolSection>
      <WrappedToolSection
        title="Fill"
        iconName="LassoSelect"
        isSectionOpen={selectedTool === FILL_TOOL}
        onClick={(open) => {
          setSelectedTool(open ? EDIT_ASSET_TOOL : FILL_TOOL);
        }}
      >
        <GenerativeFillSettings />
      </WrappedToolSection>
      <WrappedToolSection
        title="Expand"
        iconName="Expand"
        isSectionOpen={selectedTool === EXPAND_TOOL}
        onClick={(open) => {
          setSelectedTool(open ? EDIT_ASSET_TOOL : EXPAND_TOOL);
        }}
      >
        <ExpandSettings />
      </WrappedToolSection>
      <WrappedToolSection
        title="Upscale"
        iconName="ImageUpscale"
        isSectionOpen={selectedTool === UPSCALE_TOOL}
        onClick={(open) => {
          setSelectedTool(open ? EDIT_ASSET_TOOL : UPSCALE_TOOL);
        }}
      >
        <UpscaleSettings />
      </WrappedToolSection>
      {flags["variationEditTool"] && (
        <WrappedToolSection
          title="Variation"
          iconName="Layers"
          isSectionOpen={selectedTool === VARIATION_TOOL}
          onClick={(open) => {
            setSelectedTool(open ? EDIT_ASSET_TOOL : VARIATION_TOOL);
          }}
        >
          <VariationSettings />
        </WrappedToolSection>
      )}
      {flags["videoBetaProgram"] && (
        <WrappedToolSection
          title="Animate"
          iconName="Video"
          isSectionOpen={selectedTool === ANIMATE_TOOL}
          onClick={(open) => {
            setSelectedTool(open ? EDIT_ASSET_TOOL : ANIMATE_TOOL);
          }}
        >
          <AnimateSettings />
        </WrappedToolSection>
      )}
      <WrappedToolSection
        title="About"
        iconName="Info"
        isSectionOpen={infoSectionOpen}
        onClick={(open) => setInfoSectionOpen(!open)}
      >
        <InformationSection />
      </WrappedToolSection>
    </div>
  );
};

const WrappedToolSection = ({
  title,
  iconName,
  children,
  isSectionOpen,
  onClick,
}: {
  title: string;
  iconName?: IconName;
  children?: ReactNode;
  isSectionOpen: boolean;
  onClick?: (open: boolean) => void;
}) => (
  <div className="w-full flex-col gap-150 rounded-150 border-025 border-primary-rest">
    {/*
      FIXME: The px-300 padding is there to match the padding on the PanelSection component.
      This means we have spread some UI logic, which we should avoid.
    */}
    <div className="w-full px-300 py-150">
      <BaseButton
        className="w-full flex-row h-600 items-center justify-between"
        onClick={() => onClick?.(isSectionOpen)}
      >
        <div className="flex-row-center gap-150">
          {iconName && <Icon name={iconName} size="sm" />}
          <div className="text-primary label-md-semibold">{title}</div>
        </div>
        <Icon name={isSectionOpen ? "ChevronUp" : "ChevronDown"} size="sm" />
      </BaseButton>
    </div>
    {isSectionOpen && children ? children : null}
  </div>
);
